import type { PropsWithChildren } from 'react';
import React, { useEffect, useState } from 'react';
import { useLogger, useNodeEnv, useRentalManagerUser, useRouter, useSsrRedirect, useStaticApp } from '../hooks';
import { AuthCheck } from '@zg-rentals/particles-js-auth';
import { RentalManagerUserInfoWebApiHandler_get } from '@zg-rentals/ts-rental-manager-api-client';
import { BlockedUserModal } from '@zg-rentals/blocked-user-modal';
import DevCookieControls from './DevCookieControls/DevCookieControls';
import type { RentalManagerAuthCheckUserInfoFeaturesWebApiDto } from '@zg-rentals/ts-rental-manager-api-client';
import { disableLogError } from '@zg-rentals/log-error';

export type AuthCheckWrapperProps = PropsWithChildren<{
  variant?: string;
}>;

/**
 *
 * @deprecated use AuthRoute in place of Auth, do not use this component as a wrapper anymore
 */
export const AuthCheckWrapper = ({ children, variant }: AuthCheckWrapperProps) => {
  const [devModalOpen, setDevModalOpen] = React.useState(false);
  const { isInMobileWebView, brand, isProduction, zgApi, protocol, remotePort } = useNodeEnv();
  const { initialAuthComplete } = useStaticApp();
  const logger = useLogger();
  const router = useRouter();
  const setSsrRedirect = useSsrRedirect();
  const { user, updateUser } = useRentalManagerUser();
  const {
    loggedIn,
    isTwoFactorAuthEnabled,
    isTwoFactorAuthenticated,
    hasStrongAuthentication,
    userToken,
    impersonatorUserToken,
    impersonatorEmail,
  } = user;
  const [isReadyToClientSideRedirect, setIsReadyToClientSideRedirect] = useState(false);

  useEffect(() => {
    RentalManagerUserInfoWebApiHandler_get({
      includeCustomers: true,
      // @ts-ignore
      includeSummary: true,
      includeCanClaimFreeListings: true,
    })
      .then((apiRes) => {
        apiRes.features = apiRes.features || ({} as RentalManagerAuthCheckUserInfoFeaturesWebApiDto);
        updateUser(apiRes);
        return setIsReadyToClientSideRedirect(true);
      })
      .catch(() => {
        logger.error('Failed to auth user on front end');
        return setIsReadyToClientSideRedirect(true);
      });
  }, []);

  const handleAuthCheckServerSideRedirect = (redirectUrl: string) => {
    setSsrRedirect(redirectUrl);
  };

  const handleNotLoggedIn = React.useCallback(() => {
    if (isProduction) {
      return (redirectUrl: string) => {
        if (variant) {
          redirectUrl += '&variant=' + variant;
        }
        try {
          disableLogError();
          window.stop();
        } catch (exception) {
          document.execCommand('Stop');
        }
        window.location.href = redirectUrl;
      };
    }
    return () => {
      if (zgApi.includes('localhost')) {
        setDevModalOpen(true);
      } else if (zgApi.includes('comet1') || zgApi.includes('qa')) {
        // dev
        const devLoginUrl = `${zgApi}/rental-manager/featureFlags`;
        // @ts-ignore
        window.location = devLoginUrl;
      } else {
        // prod
        const devLoginUrl = `${zgApi}user/acct/login/?url=/rental-manager-api/forward%3Fprotocol=${protocol}%26port=${remotePort}%26path=${window.location.pathname}`;
        // @ts-ignore
        window.location = devLoginUrl;
      }
    };
  }, [zgApi, remotePort, isProduction, protocol]);

  if (!initialAuthComplete) return null;

  return (
    <>
      <AuthCheck
        brand={brand}
        loggedIn={loggedIn}
        hasStrongAuthentication={hasStrongAuthentication}
        impersonatorEmail={impersonatorEmail}
        impersonatorUserToken={impersonatorUserToken}
        isTwoFactorAuthEnabled={isTwoFactorAuthEnabled}
        isTwoFactorAuthenticated={isTwoFactorAuthenticated}
        isReadyToClientSideRedirect={isReadyToClientSideRedirect}
        isInWebView={isInMobileWebView}
        onRedirectCallback={handleAuthCheckServerSideRedirect}
        requireStrongAuthentication={false}
        routing={router}
        userToken={userToken}
        onNotLoggedIn={handleNotLoggedIn()}
        zillowCustomLoginPagePath="/rental-manager/login/"
      >
        {children}
      </AuthCheck>
      {!isProduction && <DevCookieControls devModalOpen={devModalOpen} setDevModalOpen={setDevModalOpen} />}
      {user?.restrictions?.isAllowed === false && user != null && <BlockedUserModal user={user} />}
    </>
  );
};
